/*
 * WELLF Spin to Win — homepage welcome wheel.
 * Vanilla port of the Claude Design component. Owns the state machine
 * (start -> spinning -> won -> done), the weighted wheel spin, slice hover/tap
 * captions, auto-open timing + per-visitor suppression. Spin-first: a click
 * anywhere on the modal (the Spin button, the wheel or the copy) spins the
 * wheel — only the close controls are excluded; the prize is revealed, then the
 * email is captured to claim it. The claim fires the shared
 * WellfKlaviyo.subscribe() with the won prize stamped on the Klaviyo profile
 * (spin_prize_id / _name / _type / _won_at / _redeemed)
 * in the SAME call for flow branching. GET20GIFT20 (type 'referral', 20% for you
 * + a 20% code to gift) is on the wheel but weight 0 (unwinnable) for now; give it
 * a weight to enable. Klaviyo sends the two codes, like a code prize.
 */
(function () {
  'use strict';

  var root = document.getElementById('wellf-spin');
  if (!root) return;

  // ── Config (from the section's data-* attributes) ──
  var LIST_ID = root.getAttribute('data-list-id') || '';
  var DELAY = parseInt(root.getAttribute('data-delay'), 10);
  if (isNaN(DELAY)) DELAY = 2000;
  var MIN_DELAY = Math.min(1500, DELAY);           // never open before the "luxury" beat
  var SUPPRESS_DAYS = parseInt(root.getAttribute('data-suppress-days'), 10);
  if (isNaN(SUPPRESS_DAYS)) SUPPRESS_DAYS = 30;
  var STORE_KEY = 'wellf_spin_seen_v1';

  // ── Behavioural events (additive; only when enable_wellf_events is on) ──
  // Wheel events belong to the WELLF event catalogue but the wheel is homepage-only,
  // so they are emitted here (not from the PDP module wellf-events.js). Consent-gated:
  // fires to Meta + GA4 only after marketing+analytics consent, which is also when
  // window.fbq/gtag load. The wheel's own analytics is Klaviyo, so no double-fire.
  var EVENTS_ON = root.getAttribute('data-wellf-events') === 'true';    // WheelSpin / WheelEmailEntered
  var SENSORS_ON = root.getAttribute('data-wellf-sensors') === 'true';  // WS3-S3 wheel instrumentation
  // Ask Shopify for the real Customer Privacy API — its privacy banner stopped
  // installing it on 2026-08-24 (method-less {cachedConsent, consentStatus} stub only),
  // which silently killed every wheel event. Only when a gate is actually on, so an
  // events-off homepage does no extra work. consentOK() below still fails closed.
  if (EVENTS_ON || SENSORS_ON) {
    try {
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }]);
      }
    } catch (e) {}
  }
  // Meta: trackCustom needs our pixel registered on the window-level fbq. It often
  // already is, so init only when it isn't; it sends no PageView either way.
  var META_ID = '1564332575347790';
  var metaInited = false;
  function metaReady() {
    if (typeof window.fbq !== 'function') return false;
    if (metaInited) return true;
    try {
      var state = window.fbq.getState && window.fbq.getState();
      var pixels = (state && state.pixels) || [];
      var already = false;
      for (var i = 0; i < pixels.length; i++) { if (pixels[i] && pixels[i].id === META_ID) { already = true; break; } }
      if (!already) window.fbq('init', META_ID);
      metaInited = true;
    } catch (e) { return false; }
    return true;
  }
  function consentOK() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      return !!(cp && cp.marketingAllowed && cp.analyticsProcessingAllowed &&
                cp.marketingAllowed() === true && cp.analyticsProcessingAllowed() === true);
    } catch (e) { return false; }
  }
  function sink(gateOn, metaName, ga4Name, params) {
    if (!gateOn || !consentOK()) return;
    var p = params ? Object.assign({}, params) : {};
    p.page = 'home';
    try { if (metaReady()) window.fbq('trackCustom', metaName, p); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', ga4Name, p); } catch (e) {}
  }
  function wheelTrack(metaName, ga4Name, params) { sink(EVENTS_ON, metaName, ga4Name, params); }
  function sensorTrack(metaName, ga4Name, params) { sink(SENSORS_ON, metaName, ga4Name, params); }

  // ── Prize table (index-aligned to the 10 wheel segments 0..9) ──
  // WEIGHTS: relative odds. APPAREL_30 (idx 7) = 0 => on the wheel but unwinnable.
  // ⚠️ TEMPORARY (2026-08-04): PCT_10 dialled up to 99% while the other prizes' fulfilment
  // is being fixed. All other winnable weights are UNCHANGED (they still sum to 54), so
  // their relative ratios are preserved and they collectively share the remaining 1%.
  // Total 5400, so % = weight / 5400: PCT_10 5346 = 99.00%, the rest = 1.00%.
  // To restore the normal odds, set PCT_10 back to 126 (total 180 => 70%).
  // APPAREL_30 (idx 7) stays 0 (unwinnable). NAMES/IDS/TYPES unchanged.
  var WEIGHTS = [5346, 13, 2, 11, 8, 5, 1, 0, 10, 4];
  var NAMES = ['10% off', 'free delivery', 'really cool stickers', '£5 pocket money', 'a sample pack', 'a mystery treat', 'club socks', '30% off apparel', 'Get 20, give 20', '25% off'];
  var IDS = ['PCT_10', 'FREE_SHIP', 'STICKERS', 'GBP_5', 'SAMPLE_PACK', 'MYSTERY', 'SOCKS', 'APPAREL_30', 'GET20GIFT20', 'PCT_25'];
  // MYSTERY (idx 5) is type 'code' (not physical): it delivers a unique 15%-off code
  // + a free pair of WELLF socks. Automated like any code prize — no address/claim
  // step. The mystery email states both prizes; the code on the order tells us to
  // pack socks. Klaviyo gives it its own email by splitting spin_prize_id=MYSTERY
  // inside the code branch.
  var TYPES = ['code', 'code', 'physical', 'code', 'physical', 'code', 'physical', 'code', 'referral', 'code'];

  // ── Elements ──
  var modal = root.querySelector('.wspin__modal');
  var disc = root.querySelector('[data-spin-disc]');
  var pointer = root.querySelector('[data-spin-pointer]');
  var caption = root.querySelector('[data-spin-caption]');
  var slices = Array.prototype.slice.call(root.querySelectorAll('[data-spin-slice]'));
  var wonEls = Array.prototype.slice.call(root.querySelectorAll('[data-spin-won]'));
  var form = root.querySelector('[data-spin-form]');
  var emailInput = form ? form.querySelector('input[type="email"]') : null;
  var errorEl = root.querySelector('[data-spin-error]');
  var goBtn = root.querySelector('[data-spin-go]');

  var stage = 'start';
  var prize = 0;
  var raf = null;
  var opened = false;
  var timer = null;
  var lastFocus = null;
  var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

  // ── Per-visitor suppression ──
  function seen() {
    if (SUPPRESS_DAYS <= 0) return false;
    try {
      var t = parseInt(localStorage.getItem(STORE_KEY), 10);
      return t ? (Date.now() - t) < SUPPRESS_DAYS * 86400000 : false;
    } catch (e) { return false; }
  }
  function markSeen() { try { localStorage.setItem(STORE_KEY, String(Date.now())); } catch (e) {} }

  // ── Caption (hover on desktop, tap on touch) ──
  var coarse = !!(window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches);
  var DEFAULT_CAPTION = coarse ? 'Tap a slice to see the treat inside.' : 'Hover a slice to see the treat inside.';
  function setCaption(txt) { if (caption) caption.textContent = txt || DEFAULT_CAPTION; }
  function activeSlice(el) { slices.forEach(function (s) { s.classList.toggle('is-active', s === el); }); }
  setCaption('');

  slices.forEach(function (el) {
    var desc = el.getAttribute('data-desc') || '';
    el.addEventListener('mouseenter', function () { if (!coarse && stage !== 'spinning') setCaption(desc); });
    el.addEventListener('mouseleave', function () { if (!coarse) { setCaption(''); activeSlice(null); } });
    el.addEventListener('click', function (e) { e.preventDefault(); if (stage === 'spinning') return; setCaption(desc); activeSlice(el); });
  });

  function setStage(s) { stage = s; if (modal) modal.setAttribute('data-spin-state', s); }

  // ── Weighted prize pick ──
  function pickPrize() {
    var total = WEIGHTS.reduce(function (a, b) { return a + b; }, 0);
    var r = Math.random() * total;
    for (var i = 0; i < WEIGHTS.length; i++) { if (r < WEIGHTS[i]) return i; r -= WEIGHTS[i]; }
    return 0;
  }

  function validEmail(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v); }

  // ── Spin-first: spinning picks the prize, then animates the wheel to it; the
  // email is captured afterwards, in the "won" state. The WHOLE modal is a spin
  // hotspot in the start state — a click anywhere (wheel, copy, the Spin button)
  // spins, so "dead clicks" become a spin + build anticipation. Only the close
  // controls ([data-spin-close] = the X and "No thanks") are excluded. ──
  function spin() {
    if (stage !== 'start') return;
    prize = pickPrize();
    wheelTrack('WheelSpin', 'wellf_wheel_spin', { detail: IDS[prize] });
    sensorTrack('WheelPlayed', 'wellf_wheel_played', {});
    runSpin();
  }
  if (modal) modal.addEventListener('click', function (e) {
    if (stage !== 'start') return;
    if (e.target.closest('[data-spin-close]')) return;
    spin();
  });

  // ── Claim: validate the email, subscribe + stamp the prize, then the success state ──
  function claim(e) {
    if (e && e.preventDefault) e.preventDefault();
    if (!emailInput) return;
    var email = (emailInput.value || '').trim();
    if (errorEl) errorEl.hidden = true;
    if (!validEmail(email)) {
      emailInput.classList.add('is-error');
      emailInput.focus();
      return;
    }
    emailInput.classList.remove('is-error');
    // WS1 hardening (2026-08-15): fire on the USER ACTION (valid email submitted),
    // BEFORE the async subscribe, so a Klaviyo failure can't drop this retargeting
    // signal. Previously it only fired inside the subscribe .then() success path.
    wheelTrack('WheelEmailEntered', 'wellf_wheel_email', {});
    form.classList.add('is-loading');
    var props = {
      spin_prize_id: IDS[prize] || IDS[0],
      spin_prize_name: NAMES[prize] || NAMES[0],
      spin_prize_type: TYPES[prize] || TYPES[0],
      spin_won_at: new Date().toISOString(),
      spin_redeemed: false
    };
    var done = function () { form.classList.remove('is-loading'); markSeen(); setStage('done'); };

    if (window.WellfKlaviyo && window.WellfKlaviyo.subscribe && LIST_ID) {
      window.WellfKlaviyo.subscribe(LIST_ID, email, { source: 'Spin to Win', properties: props })
        .then(done)
        .catch(function (err) {
          console.error('[WELLF] spin subscribe failed:', err);
          form.classList.remove('is-loading');
          if (errorEl) errorEl.hidden = false;
        });
    } else {
      done();
    }
  }
  if (form) form.addEventListener('submit', claim);

  // ── Spin animation (prize already chosen), then reveal the won state ──
  // Geometry verbatim from the design: rest 180deg, easeOutQuint, 5.6s.
  function runSpin() {
    var rest = 180;
    var finalMod = (((-prize * 36) % 360) + 360) % 360;
    var delta = 360 * 8 + ((((finalMod - rest) % 360) + 360) % 360);
    var duration = 5600;
    var startT = performance.now();
    var ease = function (t) { return 1 - Math.pow(1 - t, 5); };
    if (disc) { disc.style.transition = 'none'; disc.style.willChange = 'transform'; }
    if (pointer) pointer.style.willChange = 'transform';
    setStage('spinning');
    setCaption('');
    activeSlice(null);

    function frame(now) {
      var t = Math.min(1, (now - startT) / duration);
      var rot = rest + delta * ease(t);
      if (disc) disc.style.transform = 'rotate(' + rot + 'deg)';
      if (pointer) {
        var g = (rot / 36) - 0.5; g = g - Math.floor(g);
        var dist = Math.min(g, 1 - g);
        var defl = 13 * Math.max(0, 1 - dist / 0.28);
        pointer.style.transform = 'translateX(-50%) rotate(' + defl + 'deg)';
      }
      if (t < 1) { raf = requestAnimationFrame(frame); }
      else {
        raf = null;
        if (pointer) { pointer.style.transform = 'translateX(-50%)'; pointer.style.willChange = 'auto'; }
        if (disc) disc.style.willChange = 'auto';
        onWon();
      }
    }
    raf = requestAnimationFrame(frame);
  }

  function onWon() {
    var name = NAMES[prize] || NAMES[0];
    wonEls.forEach(function (el) { el.textContent = name; });
    setStage('won');
    if (emailInput) setTimeout(function () { try { emailInput.focus(); } catch (e) {} }, 60);
  }

  // ── Open / close ──
  function open() {
    lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    sensorTrack('WheelShown', 'wellf_wheel_shown', {});
    // Focus the Spin button so keyboard users can play immediately; on touch focus
    // the modal instead, so no keyboard springs up over the just-opened welcome.
    var first = (goBtn && !coarse) ? goBtn : modal;
    if (first) setTimeout(function () { try { first.focus(); } catch (e) {} }, 80);
  }
  function close(method) {
    if (raf) { cancelAnimationFrame(raf); raf = null; }
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    markSeen();
    method = method || 'x';
    sensorTrack('WheelClosed', 'wellf_wheel_closed', { method: method });
    if (method === 'no-thanks') sensorTrack('WheelDeclinedFunny', 'wellf_wheel_declined_funny', {});
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
  }
  // Which control closed it: the "No thanks, I hate treats" line, the backdrop, or the X.
  function closeMethod(el) {
    if (el.classList.contains('wspin__dismiss')) return 'no-thanks';
    if (el.classList.contains('wspin__backdrop')) return 'overlay';
    return 'x';
  }

  root.addEventListener('click', function (e) {
    var c = e.target.closest('[data-spin-close]');
    if (c) { e.preventDefault(); close(closeMethod(c)); }
  });

  // Esc + a lightweight focus trap while open
  document.addEventListener('keydown', function (e) {
    if (root.hidden) return;
    if (e.key === 'Escape') { close('x'); return; }
    if (e.key === 'Tab') {
      var f = Array.prototype.slice.call(modal.querySelectorAll(FOCUSABLE))
        .filter(function (el) { return el.offsetParent !== null && !el.disabled; });
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  // ── Auto-open (WS2 timing, 2026-08-16): open on scroll ≥30% OR exit-intent
  // (desktop) OR 25s dwell — whichever first. Never within the first few seconds of
  // landing (no popup over the first viewport). The wheel itself is untouched; this is
  // the timing wrapper only. The old delay_seconds setting now only sets that floor. ──
  var MIN_OPEN = Math.max(3000, MIN_DELAY);   // no popup in the first viewport-seconds
  var DWELL_MS = 25000;                        // 25s dwell fallback
  var SCROLL_TRIGGER = 30;                     // open at ≥30% scroll depth
  var landed = performance.now();

  function scrollDepthPct() {
    var doc = document.documentElement;
    var s = doc.scrollHeight - window.innerHeight;
    if (s <= 0) return 100;
    return Math.min(100, (window.scrollY / s) * 100);
  }
  function autoOpen() {
    if (opened) return;
    opened = true;
    clearTimeout(timer);
    window.removeEventListener('scroll', onScrollTrigger);
    document.removeEventListener('mouseout', onExitIntent);
    open();
  }
  // Gate every trigger behind the min-open floor; if we're still inside it, defer.
  function tryOpen() {
    if (opened) return;
    var since = performance.now() - landed;
    if (since < MIN_OPEN) { setTimeout(tryOpen, MIN_OPEN - since); return; }
    autoOpen();
  }
  function onScrollTrigger() { if (scrollDepthPct() >= SCROLL_TRIGGER) tryOpen(); }
  function onExitIntent(e) { if (e.clientY <= 0) tryOpen(); }   // desktop only (bound below)

  if (!seen()) {
    timer = setTimeout(tryOpen, DWELL_MS);
    window.addEventListener('scroll', onScrollTrigger, { passive: true });
    if (!coarse) document.addEventListener('mouseout', onExitIntent);   // exit-intent = desktop
  }
})();
